import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Building2, ShieldAlert, FileText, TrendingUp, Plus } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Kpi } from "@/components/dashboard/Kpi";
import { RiskTrendsChart } from "@/components/dashboard/RiskTrendsChart";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { subColor } from "@/lib/risk";
import api from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const bandColor = (pct) => (pct >= 50 ? "#c93838" : pct >= 20 ? "#c98a0c" : "#2f9e6b");

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const action = (
    <Link to="/app/properties"><Button variant="outline" className="gap-2" data-testid="portfolio-add-property"><Plus className="h-4 w-4" /> Add property</Button></Link>
  );

  return (
    <AppShell title="Portfolio Analytics" action={action}>
      {loading || !data ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-6" data-testid="portfolio-page">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Properties" value={data.totals.properties} icon={Building2} accent="hsl(var(--primary))" testid="portfolio-kpi-properties" />
            <Kpi label="Total assessments" value={data.totals.total_assessments} sub="across portfolio" icon={FileText} accent="hsl(var(--primary))" testid="portfolio-kpi-total" />
            <Kpi label="High-risk rate" value={`${data.totals.high_risk_pct}%`} icon={ShieldAlert} accent="#c93838" testid="portfolio-kpi-highrisk" />
            <Kpi label="Avg portfolio score" value={data.totals.avg_score} icon={TrendingUp} accent="hsl(var(--primary))" testid="portfolio-kpi-avg" />
          </div>

          <RiskTrendsChart trend={data.trend} />

          <div className="rounded-xl border bg-card" data-testid="portfolio-property-table">
            <div className="border-b px-5 py-4">
              <h3 className="font-display text-base font-semibold">Property performance</h3>
              <p className="text-xs text-muted-foreground">Ranked by high-risk rate — focus attention where it matters.</p>
            </div>
            {data.properties.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title="No portfolio data yet"
                  description="Add properties and run assessments to see portfolio analytics."
                  action={<Link to="/app/properties"><Button>Add property</Button></Link>}
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead className="text-right">Assessments</TableHead>
                      <TableHead className="text-right">Avg score</TableHead>
                      <TableHead className="text-right">High-risk rate</TableHead>
                      <TableHead className="text-center">Low / Med / High</TableHead>
                      <TableHead className="text-right">Reports</TableHead>
                      <TableHead className="text-right">Last check</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.properties.map((p) => (
                      <TableRow key={p.property_id || "unassigned"} data-testid={`portfolio-row-${p.property_id || "unassigned"}`}>
                        <TableCell>
                          <div className="font-medium">{p.name}</div>
                          {p.suburb && <div className="text-xs text-muted-foreground">{p.suburb}{p.property_type ? ` · ${p.property_type}` : ""}</div>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{p.total}</TableCell>
                        <TableCell className="text-right tabular-nums" style={{ color: subColor(p.avg_score) }}>{p.avg_score}</TableCell>
                        <TableCell className="text-right">
                          <span className="inline-flex items-center gap-2">
                            <span className="hidden h-1.5 w-16 rounded-full bg-muted sm:inline-block">
                              <span className="block h-full rounded-full" style={{ width: `${p.high_risk_pct}%`, backgroundColor: bandColor(p.high_risk_pct) }} />
                            </span>
                            <span className="tabular-nums font-medium" style={{ color: bandColor(p.high_risk_pct) }}>{p.high_risk_pct}%</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-xs">
                          <span className="text-[hsl(var(--risk-low))]">{p.low}</span> / <span className="text-[hsl(var(--risk-medium))]">{p.medium}</span> / <span className="text-[hsl(var(--risk-high))]">{p.high}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{p.reports}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">{fmtDate(p.last_assessment)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
